/*
 * FS custom: auto-add free gifts to the cart.
 * Promos are set in Theme settings → FS Free gifts. The gift is only free if a matching
 * automatic Buy X Get Y discount exists in Shopify admin — this script only adds/removes the line.
 *
 * After every cart change (Focal fires cart:updated / cart:refresh) it asks the data-only
 * section fs-free-gift-data what the cart should look like, then:
 *  - adds the gift (qty 1, hidden property _fs_gift = promo #) when the cart qualifies
 *  - keeps the gift at qty 1
 *  - removes the gift when the qualifying items leave the cart (or the promo is turned off)
 *  - respects a shopper removing the gift: it isn't re-added until the cart stops qualifying
 */
(() => {
  const SECTION = 'fs-free-gift-data';
  const STORE_KEY = 'fs-free-gift-v1';
  const vars = window.themeVariables || {};
  const routes = vars.routes || {};
  const rootUrl = routes.rootUrl || '/';
  const cartUrl = routes.cartUrl || '/cart';
  const isCartPage = vars.settings && vars.settings.pageType === 'cart';

  let running = false;
  let queued = false;
  let selfDispatch = false;
  let timer = null;

  // { declined: { promo: true }, hadGift: { promo: true } } — kept per browser tab session
  const load = () => {
    try { return JSON.parse(sessionStorage.getItem(STORE_KEY)) || {}; } catch (e) { return {}; }
  };
  const save = (state) => {
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify(state)); } catch (e) {}
  };

  const postJSON = (url, body) =>
    fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => {
      if (!r.ok) throw new Error(`${url} ${r.status}`);
      return r.json();
    });

  async function readCartState() {
    const sep = rootUrl.endsWith('/') ? '' : '/';
    const res = await fetch(`${rootUrl}${sep}?sections=${SECTION}`, { credentials: 'same-origin', cache: 'no-store' });
    const json = await res.json();
    const div = document.createElement('div');
    div.innerHTML = json[SECTION] || '';
    const tag = div.querySelector('[data-fs-free-gift]');
    return tag ? JSON.parse(tag.textContent) : { promos: [], giftLines: [] };
  }

  async function sync() {
    if (running) { queued = true; return; }
    running = true;

    try {
      const { promos, giftLines } = await readCartState();
      const state = load();
      const declined = state.declined || {};
      const hadGift = state.hadGift || {};
      const updates = {};
      const adds = [];
      const activePromos = new Set(promos.map((p) => p.promo));

      // Gift lines from promos that were turned off → remove
      giftLines.forEach((line) => {
        if (!activePromos.has(line.promo)) updates[line.key] = 0;
      });

      promos.forEach((p) => {
        if (!p.qualifies) {
          delete declined[p.promo];
          delete hadGift[p.promo];
          if (p.giftKey) updates[p.giftKey] = 0;
          return;
        }

        if (p.giftKey) {
          hadGift[p.promo] = true;
          if (p.giftQty !== 1) updates[p.giftKey] = 1;
          return;
        }

        // Qualifies but no gift: if it was there last time, the shopper removed it
        if (hadGift[p.promo]) {
          declined[p.promo] = true;
          delete hadGift[p.promo];
        }
        if (declined[p.promo]) return;

        adds.push({ id: p.variant, quantity: 1, properties: { _fs_gift: p.promo } });
      });

      let changed = false;
      if (Object.keys(updates).length) {
        await postJSON(`${cartUrl}/update.js`, { updates });
        changed = true;
      }
      if (adds.length) {
        try {
          await postJSON(`${cartUrl}/add.js`, { items: adds });
          adds.forEach((a) => { hadGift[a.properties._fs_gift] = true; });
          changed = true;
        } catch (e) {
          console.warn('[fs-free-gift] could not add gift', e);
        }
      }

      save({ declined, hadGift });
      if (changed) await refreshCart();
    } catch (e) {
      console.warn('[fs-free-gift]', e);
    } finally {
      running = false;
      if (queued) { queued = false; schedule(); }
    }
  }

  async function refreshCart() {
    if (isCartPage) { window.location.reload(); return; }

    const cart = await fetch(`${cartUrl}.js`, { credentials: 'same-origin', cache: 'no-store' }).then((r) => r.json());
    selfDispatch = true;
    try {
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
      // No sections passed → the drawer re-fetches itself. The delay lands after Focal's own
      // 600ms add-to-cart re-render, so that one can't overwrite the drawer without the gift.
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart, replacementDelay: 700 } }));
    } finally {
      selfDispatch = false;
    }
  }

  function schedule() {
    if (selfDispatch) return;
    clearTimeout(timer);
    timer = setTimeout(sync, 150);
  }

  document.documentElement.addEventListener('cart:updated', schedule);
  document.documentElement.addEventListener('cart:refresh', schedule);
  window.addEventListener('pageshow', (e) => { if (e.persisted) schedule(); });

  schedule();
})();
